<template>
  <div>
    <v-card>
      <v-card-title>Privacy Notice</v-card-title>
      <v-card-title>Haikubox Privacy Policy</v-card-title>
      <v-card-subtitle>
        Effective Date: June 20, 2022
      </v-card-subtitle>
      <v-card-text>
        Loggerhead Instruments, Inc., Haikubox and our affiliates (collectively
        “Haikubox”) respect your concerns about privacy. This Privacy Notice is
        a binding part of Haikubox Terms of Service and describes how Haikubox
        collects and processes your personal information through Haikubox
        websites, devices, products, services, online, and applications that
        reference this Privacy Notice (collectively “Haikubox Services”). This
        Privacy Notice also describes the measures we take to safeguard the
        personal information and how individuals can contact us about our
        privacy practices.</v-card-text
      >
      <v-card-text class="font-weight-bold">
        By using Haikubox Services, you are consenting to the practices
        described in this Privacy Notice. This notice may change from time to
        time, so you should check it frequently.
      </v-card-text>
      <v-card elevation="0">
        <v-card-title  style="word-break: break-word"
          >WHAT PERSONAL INFORMATION DOES HAIKUBOX COLLECT?</v-card-title
        >
        <v-card-subtitle class="font-weight-bold"
          >Information You Give Us</v-card-subtitle
        >
        <v-card-text>
          We receive and store any information you provide in connection with
          the Haikubox Services. You may choose not to provide certain
          information, but then you might not be able to take advantage of many
          of our Haikubox Services. The types of personal information we obtain
          include:
          <ol>
            <li>
              Contact information, such as name, phone number, and email and
              postal address;
            </li>
            <li>
              Account information, such as online password and other log-in
              details used to access Haikubox Services;
            </li>
            <li>
              Payment information, such as name, billing address and payment
              card details, including card number, expiration date and security
              code, which is collected and stored by our third-party payment
              processor on our behalf;
            </li>
            <li>The geolocation of your Haikubox;</li>
            <li>
              Technical information about your Haikubox product, such as your
              Wi-Fi network information and signal strength, and your Haikubox
              product’s model, serial number and software version;
            </li>
            <li>
              Data about your interactions with our websites and mobile apps;
              Social media handles, content and other data posted on our
              official social media pages;
            </li>
            <li>
              Information we obtain from third-party social media services
              (e.g., Facebook, Twitter) if you choose to link to your Haikubox
              account through these services (including when you share Haikubox
              audio or content via your social media account);
            </li>
            <li>
              Other personal information contained in content you submit to us,
              such as through our customer support tools on our websites or
              mobile apps.
            </li>
          </ol>

          <v-card-subtitle class="font-weight-bold"
            >Information We Collect from Your Use of Haikubox</v-card-subtitle
          >
          <v-card-text>
            The Haikubox Services are designed to allow you to listen to
            Haikubox audio detections from your computer or mobile device, and
            collaborate with others in your community. To provide you with these
            services, we obtain content (and related information) that is
            captured and recorded when using your Haikubox, such as:
            <ol>
              <li>Audio recordings;</li>
              <li>Comments;</li>
              <li>
                Data our products collect from their surrounding environment to
                perform their functions (such as temperature).
              </li>
            </ol>
          </v-card-text>
          <v-card-text class="font-weight-bold">
            Privacy laws in your jurisdiction may apply to your use of our
            products and services. You are solely responsible for ensuring that
            you comply with applicable law when you use our products or
            services. For example, you may need to display a notice that alerts
            visitors to your home that you are using our products or services.
            Capturing, recording, or sharing audio content that involves other
            people may affect their privacy rights.
          </v-card-text>

          <v-card-subtitle class="font-weight-bold"
            >Information We Obtain by Automated Means</v-card-subtitle
          >
          <v-card-text>
            When you interact with our products and services, we obtain certain
            information by automated means, such as cookies, web server logs,
            web beacons, and other technologies.

            <ol>
              <li>
                A “cookie” is a text file that websites send to a visitor’s
                computer or other Internet-connected device to uniquely identify
                the visitor’s browser or to store information or settings in the
                browser.
              </li>
              <li>
                A “web beacon,” also known as an Internet tag, pixel tag or
                clear GIF, links web pages to web servers and their cookies and
                may be used to transmit information collected through cookies
                back to a web server.
              </li>
            </ol>
          </v-card-text>
          <v-card-text
            >We may use these automated technologies to collect information
            about your equipment, browsing actions, and usage patterns. The
            information we obtain in this manner may include:
            <ol>
              <li>Your device IP address;</li>
              <li>Identifiers associated with your devices;</li>
              <li>Types of devices connected to Haikubox Services;</li>
              <li>Web browser and device characteristics;</li>
              <li>Language preferences;</li>
              <li>Referring/exit pages;</li>
              <li>Clickstream data;</li>
              <li>Dates and times of website or app visits.</li>
            </ol>

            <v-card-text>
              Your browser may tell you how to be notified when you receive
              certain types of cookies or how to restrict or disable certain
              types of cookies. Please note, however, that without cookies you
              may not be able to use all of the features of our online services.
              For mobile devices, you can manage how your device and browser
              share certain device data by adjusting the privacy and security
              settings on your mobile device.</v-card-text
            >
          </v-card-text>
        </v-card-text></v-card
      >
      <v-card elevation="0">
        <v-card-title  style="word-break: break-word"
          >FOR WHAT PURPOSE DOES HAIKUBOX USE YOUR PERSONAL
          INFORMATION?</v-card-title
        >
        <v-card-text>
          We use your personal information to operate, provide, develop, and
          improve the products and services that we offer our customers. The
          table below explains what we use (process) your personal information
          for and our reasons for doing so:

          <v-simple-table fixed-header height="300px">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    What we use your personal information for
                  </th>
                  <th class="text-left">
                    Our reasons
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in personalInfo" :key="item.name">
                  <td>{{ item.name }}</td>
                  <td>{{ item.reason }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-card-text>
            We also may use the personal information we collect about you in
            other ways for which we provide specific notice at the time of
            collection and obtain your consent if required by applicable
            law.</v-card-text
          ></v-card-text
        >
      </v-card>
      <v-card elevation="0">
        <v-card-title  style="word-break: break-word">WHAT ABOUT INTERNET-BASED ADVERTISING?</v-card-title>
        <v-card-text
          >When you use our websites and apps, information about your online
          activities may be collected to provide you with advertising about
          products and services tailored to your interests. This section of our
          Privacy Notice provides details and explains how to exercise your
          choices. You may see our ads on other websites or mobile apps because
          we participate in advertising networks. Ad networks allow us to target
          our messaging to individuals considering demographic data,
          individuals’ inferred interests and browsing context. These networks
          track individuals’ online activities over time by collecting
          information through automated means, including through the use of
          cookies, web server logs, web beacons and other similar technologies.
          The networks use this information to show ads that may be tailored to
          individuals’ interests, to track individuals’ browsers or devices
          across multiple websites, and to build a profile of individuals’
          online browsing activities. The information our ad networks may
          collect includes data about individuals’ visits to websites that
          participate in the relevant ad networks, such as the pages or ads
          viewed and the actions taken on the websites. This data collection
          takes place both on our websites and on third-party websites that
          participate in the ad networks. This process also helps us track the
          effectiveness of our marketing efforts. You have the right to opt out
          of receiving promotional communications at any time by:
          <ol>
            <li>Contacting us at unsubscribe@haikubox.com</li>
            <li>
              Using the “unsubscribe” link in emails or “stop” number in texts;
              or
            </li>
            <li>Updating your marketing preferences in your settings.</li>
          </ol>
          We may ask you to confirm or update your marketing preferences if you
          instruct us to provide further products and/or services in the future,
          or if there are changes in the law, regulation, or the structure of
          our business.
        </v-card-text></v-card
      >

      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >DOES HAIKUBOX SHARE THE INFORMATION IT RECEIVES?</v-card-title
        >
        <v-card-text
          >We may share our customers’ personal information only as described
          below and with our affiliates and subsidiaries.
        </v-card-text>
        <v-card-subtitle class="font-weight-bold">
          Third-Party Research:
        </v-card-subtitle>
        <v-card-text>
          We share the bird detections and audio as well as labeled data with
          the Cornell University Laboratory of Ornithology, so that they may use
          these data for scientific research and to help improve the accuracy of
          the bird call detection. 
          Bat detection data and bat recordings are shared
          with Bat Conservation International (BCI) and the North American Bat Monitoring Program (NABat) and their partners.  
          Dolphin detections and audio are shared with the Sarasota Dolphin Research Program
          and their partners for scientific research. This information sharing may include your
          unique user ID, email, and location. 
          
          You may opt out of sharing data from your
          Haikubox by changing the settings for your box on the website.</v-card-text
        >

        <v-card-subtitle class="font-weight-bold"
          >Third-Party Service Providers:
        </v-card-subtitle>
        <v-card-text
          >We employ other companies and individuals to perform certain
          functions on our behalf. Examples include storing and analyzing data,
          providing customer service, advertising, payment processing, and order
          fulfillment. We do not authorize our service providers to use or
          disclose your personal information except as necessary to perform
          services on our behalf or comply with legal requirements.
        </v-card-text>

        <v-card-subtitle class="font-weight-bold"
          >Business Partners:
        </v-card-subtitle>
        <v-card-text
          >We partner with other businesses with whom we jointly offer products
          and services, and for payment processing and fraud prevention
          purposes.
        </v-card-text>

        <v-card-subtitle class="font-weight-bold"
          >Business Transfers:
        </v-card-subtitle>
        <v-card-text
          >As we continue to develop our business, we might sell or buy other
          businesses or services. In such transactions, personal information
          generally is one of the transferred business assets but remains
          subject to the promises made in any pre-existing privacy notice
          (unless, of course, the individual consents otherwise). Also, in the
          event that Haikubox or substantially all of its assets are acquired,
          we reserve the right to transfer any personal information we have
          about you. This includes but is not limited to a merger, acquisition,
          joint venture, reorganization, divesture, dissolution, or liquidation.
        </v-card-text>

        <v-card-subtitle class="font-weight-bold"
          >Protection of Haikubox and Others:
        </v-card-subtitle>
        <v-card-text
          >We may release account and other personal information when we believe
          release is appropriate to comply with the law, enforce or apply our
          Terms of Service and other agreements, or protect the rights,
          property, or safety of Haikubox, our users, or others. This may
          include exchanging information with other companies and organizations
          for fraud protection and credit risk reduction.
        </v-card-text>

        <v-card-text>
          We do not sell or otherwise share personal information about you
          except as described in this Privacy Notice.
        </v-card-text>
      </v-card>
      <v-card elevation="0">
        <v-card-title>WHAT CHOICES DO I HAVE?</v-card-title>
        <v-card-text>
          <ol>
            <li>
              You may choose not to receive marketing e-mails from us by
              clicking on the unsubscribe link in any marketing e-mail or by
              sending an email to unsubscribe@haikubox.com.
            </li>
            <li>
              You may choose to keep your audio recordings private, so that
              other Haikubox users cannot access or listen to those recordings.
              This is configured on the Settings page.
            </li>
            <li>
              To the extent required by applicable law, you may have the right
              to request access to or delete your personal data. If you wish to
              do this, please contact customer service at support@haikubox.com.
            </li>
            <li>
              Please be advised that we do need to send you certain
              communications about our products and services, and you may not
              opt out of those communications. This includes, but is not limited
              to, information about billing.
            </li>
          </ol>
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title>NOTICE TO CALIFORNIA RESIDENTS</v-card-title>
        <v-simple-table fixed-header height="300px">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  Categories of Personal Information
                </th>
                <th class="text-left">
                  Specific Types of Personal Information Collected
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in californiaInfo" :key="item.name">
                <td>{{ item.name }}</td>
                <td>{{ item.reason }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >HOW LONG DOES HAIKUBOX KEEP PERSONAL INFORMATION?
        </v-card-title>
        <v-card-text>
          We will keep your personal information while you have an account with
          us or while we are providing products and/or services to you.
          Thereafter, we will keep your personal information as long as is
          necessary:

          <ol>
            <li>
              To respond to any questions, complaints, or claims made by your or
              on your behalf;
            </li>
            <li>To show that we treated you fairly, or</li>
            <li>To keep records required by law.</li>
          </ol>

          We will not retain your personal information for longer than necessary
          for the purpose set out in this policy. Different retention periods
          apply for different types of personal information. When it is no
          longer necessary to retain your personal information, we will delete
          or anonymize it.
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >TRANSFERRING YOUR PERSONAL INFORMATION OUT OF THE EEA</v-card-title
        >
        <v-card-text>
          To deliver services to you, it is sometimes necessary for us to share
          your personal information outside the European Economic Area (EEA),
          for example: With our office outside the EEA;  With your and our
          service providers located outside the EEA; If you are based outside
          the EEA; or Where there is an international dimension to the services
          we are providing to you. These transfers are subject to special rules
          under European and UK data protection law. If you would like further
          information, please contact us or our Data Protection Officer (see
          “How To Contact Us” below).
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >YOUR RIGHTS UNDER THE GENERAL DATA PROTECTION REGULATION
          (GDPR)</v-card-title
        >
        <v-card-text>
          Right to Access: The right to be provided with a copy of your personal
          information (the right of access).</v-card-text
        >
        <v-card-text>
          Right to Rectification: The right to require us to correct any
          mistakes in your personal information.
        </v-card-text>
        <v-card-text
          >Right to be Forgotten: The right to require us to delete your
          personal information—in certain situations. </v-card-text
        ><v-card-text
          >Right to Restriction of Processing: The right to require us to
          restrict processing of your personal information—in certain
          circumstances, e.g., if you contest the accuracy of the data </v-card-text
        ><v-card-text
          >Right to Data Portability The right to receive the personal
          information you provided to us, in a structured, commonly used and
          machine-readable format and/or transmit that data to a third party—in
          certain situations </v-card-text
        ><v-card-text
          >Right to Object The right to object: at any time to your personal
          information being processed for direct marketing (including
          profiling); in certain other situations to our continued processing of
          your personal information, e.g. processing carried out for the purpose
          of our legitimate interests. </v-card-text
        ><v-card-text
          >Right Not to be Subject to Automated Individual Decision-Making The
          right not to be subject to a decision based solely on automated
          processing (including profiling) that produces legal effects
          concerning you or similarly significantly affects you
        </v-card-text>
        <v-card-text
          >For further information on each of those rights, including the
          circumstances in which they apply, see the guidance from the UK
          Information Commissioner's Office (ICO) on individual rights under the
          General Data Protection Regulation.</v-card-text
        >
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">YOUR RIGHTS UNDER THE CCPA</v-card-title>
        <v-card-text>
          You have the right under the California Consumer Privacy Act of 2018
          (CCPA) and certain other privacy and data protection laws, as
          applicable, to exercise free of charge:</v-card-text
        >
        <v-card-subtitle
          >Disclosure of Personal Information We Collect About
          You</v-card-subtitle
        >
        <v-card-text>
          You have the right to know:
          <ol>
            <li>
              The categories of personal information we have collected about
              you;
            </li>
            <li>
              The categories of sources from which the personal information is
              collected;
            </li>
            <li>
              Our business or commercial purpose for collecting or selling
              personal information;
            </li>
            <li>
              The categories of third parties with whom we share personal
              information, if any; and
            </li>
            <li>
              The specific pieces of personal information we have collected
              about you.
            </li>
          </ol>
        </v-card-text>
        <v-card-text>
          Please note that we are not required to:
          <ol>
            <li>
              Retain any personal information about you that was collected for a
              single one-time transaction if, in the ordinary course of
              business, that information about you is not retained;
            </li>
            <li>
              Reidentify or otherwise link any data that, in the ordinary course
              of business, is not maintained in a manner that would be
              considered personal information; or
            </li>
            <li>
              Provide the personal information to you more than twice in a
              12-month period.
            </li>
          </ol>
        </v-card-text>

        <v-card-subtitle
          >Personal Information Sold or Used for a Business
          Purpose</v-card-subtitle
        >
        <v-card-text
          >We will not sell or disclose your personal information. Use of the
          Haikubox service requires the location of the haikubox device.
        </v-card-text>

        <v-card-subtitle>Right to Deletion</v-card-subtitle>
        <v-card-text
          >Subject to certain exceptions set out below, on receipt of a
          verifiable request from you, we will: • Delete your personal
          information from our records; and • Direct any service providers to
          delete your personal information from their records. Please note that
          we may not delete your personal information if it is necessary to: •
          Complete the transaction for which the personal information was
          collected, fulfill the terms of a written warranty or product recall
          conducted in accordance with federal law, provide a good or service
          requested by you, or reasonably anticipated within the context of our
          ongoing business relationship with you, or otherwise perform a
          contract between you and us; • Detect security incidents, protect
          against malicious, deceptive, fraudulent, or illegal activity; or
          prosecute those responsible for that activity; • Debug to identify and
          repair errors that impair existing intended functionality; • Exercise
          free speech, ensure the right of another consumer to exercise his or
          her right of free speech, or exercise another right provided for by
          law; • Comply with the California Electronic Communications Privacy
          Act; • Engage in public or peer-reviewed scientific, historical, or
          statistical research in the public interest that adheres to all other
          applicable ethics and privacy laws, when our deletion of the
          information is likely to render impossible or seriously impair the
          achievement of such research, provided we have obtained your informed
          consent; • Enable solely internal uses that are reasonably aligned
          with your expectations based on your relationship with us; • Comply
          with an existing legal obligation; or • Otherwise use your personal
          information, internally, in a lawful manner that is compatible with
          the context in which you provided the information.
        </v-card-text>

        <v-card-subtitle>Protection Against Discrimination</v-card-subtitle>
        <v-card-text
          >You have the right to not be discriminated against by us because you
          exercised any of your rights under the CCPA. This means we cannot,
          among other things: • Deny goods or services to you; • Charge
          different prices or rates for goods or services, including through the
          use of discounts or other benefits or imposing penalties; • Provide a
          different level or quality of goods or services to you; or • Suggest
          that you will receive a different price or rate for goods or services
          or a different level or quality of goods or services. Please note that
          we may charge a different price or rate or provide a different level
          or quality of goods and/or services to you, if that difference is
          reasonably related to the value provided to our business by your
          personal information.
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">HOW TO EXERCISE YOUR RIGHTS</v-card-title>
        <v-card-text>
          If you would like to exercise any of your rights as described in this
          Privacy Policy, please: • Email/write to us at support@haikubox.com;
          Haikubox 6576 Palmer Park Circle Sarasota, FL 34238. Please note that
          you may only make a CCPA-related data access or data portability
          disclosure request twice within a 12-month period. If you choose to
          contact directly by email, or in writing, you will need to provide us
          with: • Enough information to identify you (e.g., your full name,
          address and customer or matter reference number); • Proof of your
          identity and address (e.g., a copy of your driving license or passport
          and a recent utility or credit card bill); and • A description of what
          right you want to exercise and the information to which your request
          relates. We are not obligated to make a data access or data
          portability disclosure if we cannot verify that the person making the
          request is the person about whom we collected information, or is
          someone authorized to act on such person’s behalf. Any personal
          information we collect from you to verify your identity in connection
          with your request will be used solely for the purposes of
          verification.
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">HOW TO FILE A GDPR COMPLAINT</v-card-title>
        <v-card-text>
          We hope that we can resolve any query or concern you raise about our
          use of your information. The General Data Protection Regulation also
          gives you right to lodge a complaint with a supervisory authority, in
          the European Union (or European Economic Area) state where you work,
          normally live, or where any alleged infringement of data protection
          laws occurred. The supervisory authority in each country can be found
          at https://edpb.europa.eu/about-edpb/board/members_en.
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">OTHER THIRD-PARTY SERVICES AND FEATURES</v-card-title>
        <v-card-text>
          Our website and mobile apps may provide links to other online services
          for your convenience and information, and may include third-party
          features such as apps, tools, payment services, widgets and plug-ins
          (e.g., Facebook or Twitter buttons). If you opt-in to push notifications, 
          information about your phone will be stored by OneSignal, so that we 
          may send appropriate notifications. These third-party services and
          features may operate independently from us. The privacy practices of
          the relevant third parties, including details on the information they
          may collect about you, is subject to the privacy statements of these
          parties, which we strongly suggest you review. To the extent any
          linked online services or third-party features are not owned or
          controlled by us, Haikubox is not responsible for these third parties’
          information practices. By using or engaging with such services and
          features, you are requesting and authorizing us to transfer or provide
          access to information on your behalf, for example, your account ID,
          email address, data about your Haikubox device usage or collected by
          your Haikubox device, etc.</v-card-text
        >
      </v-card>
      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >HOW DOES HAIKUBOX PROTECT PERSONAL INFORMATION</v-card-title
        ><v-card-text>
          We maintain administrative, technical and physical safeguards designed
          to protect personal information against accidental, unlawful or
          unauthorized destruction, loss, alteration, access, disclosure or
          use.</v-card-text
        >
      </v-card>
      <v-card elevation="0">
        <v-card-title style="word-break: break-word"
          >HOW CAN I REQUEST DELETION OF MY PERSONAL INFORMATION?</v-card-title
        ><v-card-text>
          You may have the right under the California Consumer Privacy Act, to
          request information about the collection of your personal information
          by Haikubox, or access to or deletion of your personal information. If
          you wish to do any of these things, please contact
          support@haikubox.com. Haikubox will not discriminate against any
          consumer for exercising their rights under the California Consumer
          Privacy Act.</v-card-text
        >
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">DOES HAIKUBOX SELL PERSONAL INFORMATION?</v-card-title
        ><v-card-text>
          No. In the twelve months prior to the effective date of this
          Disclosure, Haikubox has not sold any personal information of
          consumers, as those terms are defined under the California Consumer
          Privacy Act.
        </v-card-text>
      </v-card>

      <v-card elevation="0">
        <v-card-title style="word-break: break-word">POLICIES FOR CHILDREN</v-card-title
        ><v-card-text>
          We recognize the importance of protecting children's online privacy.
          Haikubox's products and services are intended for a general audience
          and are not directed to children. We do not knowingly collect personal
          information online from children under the age of 13.</v-card-text
        >
      </v-card>
      <v-card elevation="0">
        <v-card-title style="word-break: break-word">UPDATES TO OUR PRIVACY NOTICE </v-card-title
        ><v-card-text>
          This Privacy Notice may be updated periodically and without prior
          notice to you to reflect changes in our personal information
          practices. We will post a notice on our websites and mobile apps to
          notify you of significant changes to our Privacy Notice and indicate
          at the top of the notice when it was most recently
          updated.</v-card-text
        >
      </v-card>
      <v-card-title style="word-break: break-word">HOW TO CONTACT US</v-card-title
      ><v-card-text>
        If you have any questions about this Privacy Notice, or if you would
        like us to update information we have about you, change your preferences
        or exercise other applicable data protection rights, please contact us
        by e-mail at support@haikubox.com or write to us at: Loggerhead
        Instruments-Haikubox 6576 Palmer Park Circle Sarasota, FL
        34238</v-card-text
      >
    </v-card>
  </div>
</template>

<script>
  export default {
    data() {
      return {
        californiaInfo: [
          {
            name:
              "Identifiers (e.g., a real name, alias, postal address, unique personal identifier, online identifier, Internet Protocol address, email address, account name, social security number, driver’s license number, passport number, bank account number, credit card number, debit card number, any other financial information or other similar identifiers)",
            reason:
              "Full name; City, State, Country and Zip Code; Email Address; Username; Bank account number; Credit card number; Debit card number; Any other financial information that you provide",
          },
          {
            name:
              "Personal Information categories listed in the California Customer Records statute (Cal. Civ. Code ",
            reason: "Full name; City, State, Country and Zip Code",
          },
          {
            name:
              "Characteristics of protected classifications under California or federal law.",
            reason: "Age, Gender",
          },
          {
            name:
              "Commercial information (e.g., records of personal property, products or services purchased, obtained, or considered or other purchasing or consuming histories or tendencies)",
            reason:
              "Products and/or services purchased, obtained or considered; Other purchasing or consuming histories or tendencies ",
          },
          {
            name:
              "Internet or other electronic network activity information (e.g., browsing history, search history, and information regarding a consumer’s interaction with an internet web site, application, or advertisement)",
            reason:
              "Crash reports for analyzing the health of our devices and services  (Please note that no information listed in this category is specific to any individual user or account)",
          },
          {
            name: "Geolocation data",
            reason:
              "•	The GPS location of your Haikubox; The location of your mobile device",
          },
          {
            name:
              "Audio; electronic, visual, thermal, olfactory, or similar information",
            reason: "The audio recordings stored on your Haikubox",
          },
          {
            name:
              "Inferences drawn from any of the information identified above to create a profile about a consumer reflecting the consumer’s preferences, characteristics, psychological trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes ",
            reason: "Interests in other Haikubox products or services. ",
          },
        ],
        personalInfo: [
          {
            name: "• To provide our products and services to you",
            reason:
              "For the performance of our contract with you or to take steps at your request before entering into a contract",
          },
          {
            name: "•	To prevent and detect fraud against you or Haikubox",
            reason:
              "For our legitimate interests or those of a third party, i.e., to minimize fraud that could be damaging for us and for you ",
          },
          {
            name:
              "•	Conducting checks to identify our customers and verify their identity •	Screening for financial and other sanctions or embargoes  •	Other processing necessary to comply with professional, legal, and regulatory obligations that apply to our business, e.g., under health and safety regulation or rules issued by our professional regulator  •	Gathering and providing information required by or relating to audits, enquiries or investigations of regulatory bodies",
            reason: "To comply with our legal and regulatory obligations",
          },
          {
            name:
              "•	Ensuring business policies are adhered to, e.g., Policies covering security and internet use listed in our Terms of Service",
            reason:
              "For our legitimate interest or those of a third party, i.e., to make sure we are following our own internal procedures so we can deliver the best service to you",
          },
          {
            name:
              "•	Operational reasons, such as improving efficiency, training and quality control",
            reason:
              "For our legitimate interest of those of a third party, i.e., to be as efficient as we can so we can deliver the best service for you at the best price",
          },
          {
            name:
              "•	Ensuring confidentiality of commercially sensitive information",
            reason:
              "For our legitimate interest or those of a third party, i.e., to protect trade secrets and other commercially valuable information and to comply with our legal and regulatory obligations",
          },
          {
            name:
              "•	Statistical analysis to help us manage our business, e.g., in relation to our financial performance, customer base, product range or other efficiency measures",
            reason:
              "For our legitimate interest or those of a third party, i.e., to be as efficient as we can so we can deliver the best service for you at the best price",
          },
          {
            name:
              "•	Preventing unauthorized access and modifications to systems",
            reason:
              "For our legitimate interest or those of a third party, i.e., to prevent and detect criminal activity that could be damaging for us and for you and to comply with our legal and regulatory obligations",
          },
          {
            name: "•	Updating and enhancing customer records",
            reason:
              "For the performance of our contract with you or to take steps at your request before entering into a contract; to comply with our legal and regulatory obligations; and for our legitimate interests or those of a third party, e.g., making sure that we can keep in touch with our customers about existing orders and new products",
          },
          {
            name: "•	Statutory returns ",
            reason: "To comply with our legal and regulatory obligations",
          },
          {
            name:
              "•	Ensuring safe working practices, staff administration and assessment",
            reason: "To comply with our legal and regulatory obligations",
          },
          {
            name:
              "•	Marketing our services and those of selected third parties to: Existing and former customers, Third parties who have previously expressed an interest in our services, Third parties with whom we have had no previous dealings",
            reason:
              "For our legitimate interests or those of a third party, i.e., to promote our business to existing and former customers",
          },
          {
            name:
              "•	Allow you to interact with certain third-party products and/or services",
            reason:
              "For our legitimate interests or those of a third party, i.e. to enable you to link to, or view content from third-party sites within our services",
          },
        ],
      };
    },
  };
</script>
